import { polyquantOutcome } from '../lib/polyquantLabels';
/**
 * Куда ставить — одним словом на ногу.
 *
 * Отдельный модуль, а не место в строке или в карточке: подпись исхода нужна
 * обеим, а строка и карточка уже ссылаются друг на друга (`legOrder` живёт в
 * строке, карточка его берёт), и третья ссылка замкнула бы круг.
 */

import { nativeOutcome } from '../lib/nativeMarket';
import { marketFamily, marketLabel, roundWinSaid, sideLabel, type Fork } from '../lib/protocol';
import type { RowLeg } from '../lib/row';
import { isCodeName, isUrlName } from './matchTitle';

/** Нога для подписи: `who` опционален — старые тесты и короткий side его не несут. */
type SaidLeg = Pick<RowLeg, 'side' | 'name' | 'source'> & {
  lay?: boolean;
  who?: readonly string[] | null;
};

type SaidFork = Pick<Fork, 'market' | 'sport'> & {
  id?: string;
  legs?: readonly { s: string; participants?: readonly string[] | null }[];
  participants?: readonly string[] | null;
};

/**
 * Что именно отмечать на площадке.
 *
 * Исход живёт в `side`: зеркало отдаёт готовую фразу («Тотал меньше 19.5»),
 * прод — код (`H`, `A`, `O`, `U`). Имя участника вместо исхода подставлять
 * нельзя: у зеркала оно пустое, а раскладка берёт участника по индексу стороны,
 * и индекс равен единице только для кода `A`. На фразе он всегда нулевой — и
 * обе ноги подписывались ОДНИМ игроком: «ставка на Mark Dudbridge» и слева, и
 * справа. Экран при этом молчал о единственном, чего в нём ищут: куда ставить,
 * чтобы вилка была вилкой, а не двумя ставками на одно и то же.
 */
/**
 * Английские фразы донора → русские. Лента русскоязычная, и «Win at least in
 * one period 2nd team» в ней читается как поломка, а не как исход.
 *
 * Переводятся только ФРАЗЫ, известные целиком (по живому замеру 07.08 их
 * меньше процента ног). Догадка по словам запрещена: «First Team 1» может
 * значить и «первая забьёт», и «выиграет первый период», и перевести его
 * наугад значит отправить человека ставить не на то. Незнакомое остаётся как
 * есть — уродливое, но честное.
 */
const ENGLISH: ReadonlyArray<[RegExp, (m: RegExpMatchArray) => string]> = [
  [
    /^win at least in one period (1st|2nd) team$/i,
    (m) => `команда ${m[1] === '1st' ? 1 : 2} возьмёт хотя бы один период`,
  ],
  [
    /^no win at least in one period (1st|2nd) team$/i,
    (m) => `команда ${m[1] === '1st' ? 1 : 2} не возьмёт ни одного периода`,
  ],
  [/^clean sheet (1st|2nd) team$/i, (m) => `команда ${m[1] === '1st' ? 1 : 2} не пропустит`],
  [/^no clean sheet (1st|2nd) team$/i, (m) => `команда ${m[1] === '1st' ? 1 : 2} пропустит`],
];

const translated = (side: string): string => {
  for (const [test, say] of ENGLISH) {
    const hit = side.match(test);
    if (hit !== null) return say(hit);
  }
  return side;
};

function namesOf(leg: SaidLeg, fork?: SaidFork): readonly string[] {
  const clean = (list: readonly string[] | null | undefined): string[] =>
    (list ?? []).map((one) => one.trim()).filter((one) => one !== '' && !isCodeName(one));
  const own = clean(leg.who);
  const raw = own.length > 0 ? own : clean(fork?.participants);
  if (raw.length >= 2) return raw;
  const glued = raw[0] ?? '';
  const bits = glued.split(/\s+(?:—|–|−|-|vs\.?|versus|против)\s+/i).map((one) => one.trim()).filter(Boolean);
  return bits.length >= 2 ? [bits[0] ?? '', bits[bits.length - 1] ?? ''] : raw;
}

/**
 * «П1»/«Тотал Б 2.5» донора → имя команды и «больше».
 *
 * Участника по индексу стороны подставлять нельзя: на фразе индекс всегда
 * нулевой, и обе плитки получали одного игрока. Здесь меняем только токен,
 * который сам называет сторону (П1/П2), и букву тотала перед числом (Б/М).
 * «тотал больше 19.5» и «First Team 1» не трогаем — это уже фраза или загадка.
 */
function donorSaid(text: string, who: readonly string[]): string {
  const raw = text.trim();
  if (raw === '') return raw;
  const home = who[0] ?? '';
  const away = who[1] ?? '';

  const tot = raw.match(/^тотал\s+([БМ])\s*(\d+(?:[.,]\d+)?)(?:\s*[·•]\s*(.+))?$/i);
  if (tot !== null) {
    const letter = tot[1] ?? '';
    const line = (tot[2] ?? '').replace(',', '.');
    const word = /[Бб]/.test(letter) ? 'больше' : 'меньше';
    const period = (tot[3] ?? '').trim();
    return period === '' ? `тотал ${word} ${line}` : `тотал ${word} ${line}, ${period}`;
  }

  const win = raw.match(/^победа\s+п([12])(?:\s*[·•.,]\s*матч)?$/i);
  if (win !== null) {
    const name = (win[1] === '1' ? home : away).trim();
    return name === '' ? raw : name;
  }

  const hcp = raw.match(
    /^фора\s+п([12])\s*([+\-−]?\d+(?:[.,]\d+)?)(?:\s*[·•]\s*(.+))?$/i,
  );
  if (hcp !== null) {
    const name = (hcp[1] === '1' ? home : away).trim();
    if (name === '') return raw;
    const line = (hcp[2] ?? '').replace('−', '-').replace(',', '.');
    return `${name} ${line}`;
  }

  if (home === '' && away === '') return raw;
  return raw
    .replace(/(^|[^A-Za-zА-Яа-яЁё0-9])[Пп]([12])(?![A-Za-zА-Яа-яЁё0-9])/g, (all, pre, n) => {
      const name = (n === '1' ? home : away).trim();
      return name === '' ? all : `${pre}${name}`;
    })
    .replace(/\s*[·•]\s*матч\s*$/i, '');
}

function teamOf(leg: SaidLeg, fork?: SaidFork): string {
  const who = namesOf(leg, fork);
  const said = `${leg.side ?? ''} ${leg.name ?? ''}`;
  const fromPhrase = /п\s*1\b/i.test(said) ? 'H' : /п\s*2\b/i.test(said) ? 'A' : '';
  const code = sideCode((leg.side ?? '').trim()) || fromPhrase;
  const name = code === 'H' ? who[0] : code === 'A' ? who[1] : '';
  const team = (name ?? '').trim();
  return team !== '' && !isCodeName(team) ? team : '';
}

export function pick(leg: SaidLeg): string {
  // Зеркальная нога может приехать без `side` (канал V до 18.09). Падать на
  // trim — белый экран всей ленты; прочерк честнее, чем спрятать витрину.
  const who = namesOf(leg);
  const native = nativeOutcome(leg.name, who) ?? nativeOutcome(leg.side ?? '', who);
  if (native !== null) return leg.lay === true ? `не будет: ${native}` : native;
  const team = teamOf(leg);
  if (team !== '' && (sideCode((leg.side ?? '').trim()) !== '' || /победа\s+п[12]/i.test(`${leg.side ?? ''} ${leg.name ?? ''}`))) {
    if (leg.lay === true) return `не будет: ${team}`;
    return team;
  }
  const side = donorSaid(translated((leg.side ?? '').trim()), who);
  // Нога «против»: слово донора «ПРОТИВ» капсом не читается — владелец продукта
  // принял его за вторую ставку на то же самое. Подпись говорит, чего НЕ
  // случится: «не будет: Тотал больше 6.5». Служебный префикс рынка («2-way:»)
  // человеку не нужен и срезается.
  if (leg.lay === true) {
    const what = donorSaid(
      side
        .replace(/^ПРОТИВ\s+/i, '')
        .replace(/^\d+-way:\s*/i, '')
        .trim(),
      who,
    );
    return `не будет: ${what === '' ? side : what}`;
  }
  if (side === 'Y' || /^yes$/i.test(side) || side.toLowerCase() === 'да') return 'да';
  if (side === 'N' || /^no$/i.test(side) || side.toLowerCase() === 'нет') return 'нет';
  if (side.length > 2) return side;
  // «больше»/«меньше» — сам исход, имя участника на тотале только помешает.
  if (side === 'O' || side === 'U') return sideLabel(side);
  if (leg.name !== '' && leg.name !== leg.source) return donorSaid(leg.name, who);
  const label = sideLabel(side);
  return label === '' ? '—' : label;
}

const YES_NO = /^(да|нет)$/i;

/** Теннис и настольный: P1 в ключе — сет, не карта. */
const SET_SPORTS = new Set(['tennis', 'table_tennis']);

type Key = {
  market: string;
  scope: string;
  period: string;
  side: string;
  line: string;
  subject: string;
};

function parseKey(market: string | undefined): Key | null {
  const parts = (market ?? '').split(':');
  if (parts.length !== 6) return null;
  if (!/^(WIN|TTL|ITTL|HCP|CRN|CRD|BTTS)$/i.test(parts[0] ?? '')) return null;
  return {
    market: (parts[0] ?? '').toUpperCase(),
    scope: (parts[1] ?? '').toUpperCase(),
    period: parts[2] ?? '',
    side: parts[3] ?? '',
    line: parts[4] ?? '',
    subject: (parts[5] ?? '').toUpperCase(),
  };
}

/**
 * Исход для карточки и строки: чего тотал и какой отрезок, не «больше 2.5».
 *
 * «Тотал больше 2.5» не говорит, голов это или угловых. Голое имя в плитке
 * не говорит, победа это или сет: слово «победа» из шапки окна не читается.
 * Фора без числа — та же слепота: «фора карт paiN Gaming» не говорит +1.5 или
 * −1.5. Короткая `pick` — сырьё; здесь фраза собирается из ключа и вида спорта.
 */
export function betOn(leg: SaidLeg, fork: SaidFork): string {
  // Polyquant supplies complete outcomes, including map/line/team. Never infer
  // their meaning from leg order or the other leg's market text.
  if (fork.id?.startsWith('f7:')) return leg.name || leg.side || '';
  if (fork.id?.startsWith('pq:')) return polyquantOutcome(leg.name || leg.side);
  const who = namesOf(leg, fork);
  const native = nativeOutcome(leg.name, who) ?? nativeOutcome(leg.side ?? '', who);
  if (native !== null) return leg.lay === true ? `не будет: ${native}` : native;
  const raw = pick({ ...leg, who });
  if (leg.lay === true) return raw;
  const key = parseKey(fork.market);
  const sport = fork.sport ?? '';
  const family = sayFamily(key, marketFamily({ market: fork.market, sport } as Fork));
  const said = `${leg.side ?? ''} ${leg.name ?? ''}`;
  const period = periodOf(key, fork.market, sport, said);
  const unit = unitOf(key, fork.market, sport);

  if (key?.market === 'BTTS' || family === 'other') {
    const what = otherWhat(fork.market, sport);
    if (what !== '' && YES_NO.test(raw)) return `${what} — ${raw}`;
    if (what !== '' && !raw.toLowerCase().includes(what.toLowerCase())) return `${what} — ${raw}`;
  }

  if (isTotal(family, key)) {
    const line = sideLine(raw, key, leg.side ?? '');
    const head = key?.market === 'ITTL' || /^инд/i.test(raw) ? 'инд. тотал' : 'тотал';
    const who = key?.market === 'ITTL' ? whoOf(key.subject) : '';
    return [period, head, unit, who, line].filter((bit) => bit !== '').join(' ');
  }
  if (family === 'handicap') {
    const body = /фора|гандикап|handicap/i.test(raw) ? raw.replace(/^фору\s+/i, '') : raw;
    // The fork key belongs to the first native leg. H/A are local to each
    // venue, so reversed participant order can make both opposite legs H.
    const pair = fork.legs;
    const index = pair?.length === 2 && pair[0]?.s !== pair[1]?.s
      ? pair.findIndex((one) => one.s === leg.source) : -1;
    const line = hasHcpNumber(body) ? '' : index >= 0 && key?.line
      ? formatHcp(index === 0 ? key.line : flipHcp(key.line))
      : hcpLineForLeg(key, leg.side ?? '');
    return [period, 'фора', unit, line, body].filter((bit) => bit !== '').join(' ');
  }
  if (family === 'double') {
    return /шанс/i.test(raw) ? raw : `двойной шанс ${raw}`;
  }
  if (family === 'win') {
    const team = teamOf(leg, fork);
    const said = team !== '' ? team : raw;
    const round = roundWinSaid(fork.market, sport);
    if (round !== null) return said !== '' && said !== '—' ? `${round} — ${said}` : round;
    if (period !== '') return `${period} — ${said}`;
    if (/^(ничья|x)$/i.test(said)) return said;
    if (team !== '') return `победа ${team}`;
    if (/побед/i.test(raw) && !/победа\s+п[12]/i.test(raw)) return raw;
    return /побед/i.test(raw) ? raw : `победа ${raw}`;
  }
  return raw;
}

/** Карта и команда — две строки на плитке, чтобы имя не ломалось по словам. */
export function betFace(text: string): { lead: string | null; name: string } {
  const cut = text.indexOf(' — ');
  if (cut <= 0 || cut >= text.length - 3) return { lead: null, name: text };
  return { lead: text.slice(0, cut), name: text.slice(cut + 3) };
}

/** «3-я / 4-я карта» — фишка сита, не вид ставки: подпись берёт вид из ключа. */
function sayFamily(key: Key | null, family: string): string {
  if (family !== 'map3' && family !== 'map4') return family;
  switch (key?.market) {
    case 'WIN':
      return 'win';
    case 'HCP':
      return 'handicap';
    case 'TTL':
    case 'ITTL':
      return 'total';
    default:
      return 'other';
  }
}

function isTotal(family: string, key: Key | null): boolean {
  return (
    family === 'total' || key?.market === 'CRN' || key?.market === 'CRD' || key?.market === 'TTL'
  );
}

/** В фразе уже есть линия (+1.5 / −6.5). Голое «команда 1» — не линия. */
function hasHcpNumber(text: string): boolean {
  return /(?:^|\s)[+\-−]?\d+[.,]\d+(?:\s|$)/.test(text) || /(?:^|\s)[+\-−]\d+(?:\s|$)/.test(text);
}

function sideCode(side: string): 'H' | 'A' | '' {
  const one = side.trim();
  if (one === 'H' || one === '1') return 'H';
  if (one === 'A' || one === '2') return 'A';
  return '';
}

function flipHcp(line: string): string {
  const n = line.trim().replace(',', '.').replace('−', '-').replace(/^\+/, '');
  if (n === '' || /^-?0(?:\.0+)?$/.test(n)) return '0';
  return n.startsWith('-') ? n.slice(1) : `-${n}`;
}

function formatHcp(line: string): string {
  const n = line.trim().replace(',', '.').replace('−', '-').replace(/^\+/, '');
  if (n === '') return '';
  if (/^-?0(?:\.0+)?$/.test(n)) return '0';
  return n.startsWith('-') ? n : `+${n}`;
}

/**
 * Линия этой ноги. Ключ вилки несёт сторону первой — второй плитке знак
 * переворачиваем, иначе обе напишут +1.5 при живых +1.5 и −1.5.
 */
function hcpLineForLeg(key: Key | null, legSide: string): string {
  if (key === null || key.line === '') return '';
  const here = sideCode(legSide);
  const listed = sideCode(key.side);
  const line = here !== '' && listed !== '' && here !== listed ? flipHcp(key.line) : key.line;
  return formatHcp(line);
}

function sideWord(side: string): string {
  if (side === 'O' || side === 'больше') return 'больше';
  if (side === 'U' || side === 'меньше') return 'меньше';
  return '';
}

/**
 * Сторона тотала — у ноги. Ключ вилки несёт сторону первой ноги, и подставлять
 * её второй плитке значит подписать Over и Under одним словом (Z5J2, D3H6).
 *
 * Линия и единица — общие, их по-прежнему берём из ключа. Фразу донора
 * оставляем, только если она уже называет ту же сторону, что `leg.side`.
 */
function sideLine(raw: string, key: Key | null, legSide: string): string {
  const line = key?.line ?? '';
  const fromLeg = sideWord(legSide.trim());
  const hit = raw.match(/(больше|меньше)\s*[\d]+(?:[.,]\d+)?/i);
  if (fromLeg !== '') {
    if (hit !== null) {
      const text = hit[0].toLowerCase().replace(',', '.');
      if (text.startsWith(fromLeg)) return text;
    }
    if (line !== '') return `${fromLeg} ${line}`;
    return fromLeg;
  }
  if (hit !== null) return hit[0].toLowerCase().replace(',', '.');
  if (key !== null) {
    const side = sideWord(key.side);
    if (side !== '' && key.line !== '') return `${side} ${key.line}`;
    if (side !== '') return side;
  }
  return raw.replace(/^(инд\.\s*)?тотал\s+/i, '').trim();
}

function unitOf(key: Key | null, market: string | undefined, sport: string): string {
  const fromWords = unitFromText(`${market ?? ''} `);
  if (key === null) return fromWords;
  if (key.market === 'CRN' || key.scope === 'CR') return 'угловых';
  if (key.market === 'CRD') return 'карточек';
  if (key.scope === 'PE') return SET_SPORTS.has(sport) ? 'сетов' : 'карт';
  if (key.scope === 'PO') return pointsUnit(sport);
  return fromWords;
}

function pointsUnit(sport: string): string {
  if (sport === 'soccer') return 'голов';
  if (sport === 'hockey') return 'шайб';
  if (sport === 'tennis' || sport === 'table_tennis') return 'геймов';
  if (sport === 'baseball') return 'ранов';
  if (sport === 'cs2' || sport === 'valorant' || sport === 'rsix') return 'раундов';
  if (sport === 'dota2' || sport === 'lol' || sport === 'mlbb') return 'убийств';
  if (sport === 'basketball' || sport === 'american_football' || sport === 'volleyball') {
    return 'очков';
  }
  return 'очков';
}

function unitFromText(text: string): string {
  const m = text.toLowerCase();
  if (/угл|corner/.test(m)) return 'угловых';
  if (/карточек|желт|жёлт|\bcards?\b/.test(m)) return 'карточек';
  if (/гол|goal/.test(m)) return 'голов';
  if (/гейм|games?/.test(m)) return 'геймов';
  if (/раунд|rounds?/.test(m)) return 'раундов';
  if (/шайб/.test(m)) return 'шайб';
  if (/ран(?!а)|runs?/.test(m)) return 'ранов';
  return '';
}

function periodOf(
  key: Key | null,
  market: string | undefined,
  sport: string,
  said = '',
): string {
  if (key !== null) {
    const coded = periodCode(key.period, sport);
    if (coded !== '') return coded;
  }
  return periodFromText(market ?? '') || periodFromText(said);
}

function periodCode(period: string, sport: string): string {
  if (period === '' || period === 'M' || period === 'U') return '';
  const sets: Record<string, string> = {
    S1: '1-й сет',
    S2: '2-й сет',
    S3: '3-й сет',
    S4: '4-й сет',
    S5: '5-й сет',
    H1: '1-й тайм',
    H2: '2-й тайм',
    Q1: '1-я четверть',
    Q2: '2-я четверть',
    Q3: '3-я четверть',
    Q4: '4-я четверть',
  };
  if (sets[period] !== undefined) return sets[period]!;
  const n = period.match(/^P([1-9])$/);
  if (n !== null) return SET_SPORTS.has(sport) ? `${n[1]}-й сет` : `${n[1]}-я карта`;
  const inn = period.match(/^I([1-9])$/);
  if (inn !== null) return `${inn[1]}-й иннинг`;
  return '';
}

function periodFromText(market: string): string {
  const m = market.toLowerCase();
  const set = m.match(/(\d+)[^\d]{0,8}сет/);
  if (set !== null) return `${set[1]}-й сет`;
  if (/первый сет|1-й сет|сет\s*1/.test(m)) return '1-й сет';
  const half = m.match(/(\d+)[^\d]{0,8}тайм/);
  if (half !== null) return `${half[1]}-й тайм`;
  const cardYa = m.match(/(\d+)-я\s+карта/);
  if (cardYa !== null) return `${cardYa[1]}-я карта`;
  const card = m.match(/карта\s+(\d+)/i);
  if (card !== null) return `${card[1]}-я карта`;
  return '';
}

function whoOf(subject: string): string {
  if (subject === 'H') return 'хозяев';
  if (subject === 'A') return 'гостей';
  return '';
}

function otherWhat(market: string | undefined, sport?: string): string {
  const m = (market ?? '').toLowerCase();
  if (
    m.startsWith('btts') ||
    (m.includes('обе') && m.includes('заб')) ||
    (m.includes('both') && m.includes('score'))
  ) {
    return 'обе забьют';
  }
  const labeled = marketLabel(market, sport);
  if (labeled === '' || labeled === '—' || labeled === market) return '';
  return labeled;
}

/**
 * Название матча в порядке плиток, не домашний–гость из протокола.
 *
 * Ноги идут по риску отказа (`legOrder`): первая плитка часто гостевая.
 * Шапка по `participants` тогда читается наоборот от вилки.
 */
export function matchTitleInOrder(fork: SaidFork, legs: readonly SaidLeg[]): string {
  const names: string[] = [];
  for (const leg of legs) {
    const team = teamOf(leg, fork);
    if (team !== '' && !names.includes(team)) names.push(team);
  }
  const listed = [
    ...(fork.participants ?? []),
    ...legs.flatMap((leg) => leg.who ?? []),
    ...(fork.legs ?? []).flatMap((leg) => leg.participants ?? []),
  ]
    .map((one) => one.trim())
    .filter((one) => one !== '' && !isCodeName(one) && !isUrlName(one));
  const unique = [...new Set(listed)];
  if (names.length >= 2) return names.slice(0, 2).join(' — ');
  if (names.length === 1) {
    const other = unique.find((one) => one !== names[0]);
    if (other !== undefined) return `${names[0]} — ${other}`;
  }
  if (unique.length >= 2) return unique.slice(0, 2).join(' — ');
  if (unique.length === 1) return unique[0] ?? 'матч';
  return 'матч';
}

/**
 * Те же подписи для ленты, но с отрезанным общим началом: «больше 19.5» и
 * «меньше 19.5» вместо двух «Тотал …» подряд.
 *
 * В строке место на несколько слов, и общее начало в них уходит зря: рынок уже
 * назван подзаголовком строки, а различаются ноги одним словом — ровно тем,
 * ради которого строку и открывают.
 *
 * Двух вещей не делаем никогда. Не режем, пока в подписи не останется хотя бы
 * одно слово. И не оставляем подпись, начинающуюся с числа: «1 −6.5» человек
 * читать не станет, поэтому слово перед числом возвращаем на место — выйдет
 * «команда 1 −6.5».
 */
export function shortPicks(legs: readonly RowLeg[]): string[] {
  const labels = legs.map((leg) => pick(leg));
  if (labels.length !== 2) return labels;
  // Совпавшие подписи — уже беда, и резать их незачем: различать нечего, а
  // отрезанное начало вместе с общим убрало бы и рынок.
  if (labels[0] === labels[1]) return labels;
  const words = labels.map((label) => label.split(' '));
  let common = 0;
  while (
    words.every((one) => one.length > common + 1) &&
    words.every((one) => one[common] === words[0]![common])
  ) {
    common += 1;
  }
  if (common === 0) return labels;
  const start = /^[-+\d\u2212]/.test(words[0]![common] ?? '') ? common - 1 : common;
  return words.map((one) => one.slice(start).join(' '));
}
